/* eslint-disable import/no-extraneous-dependencies */
import '../sass/app.scss';
import { createApp, h, provide } from 'vue';
import {
  init as sentryInit,
  BrowserTracing,
  vueRouterInstrumentation,
  setTag,
  getClient,
} from '@sentry/vue';
import { ApolloClients, provideApolloClient } from '@vue/apollo-composable';
import App from './components/App.vue';
import router from '~/router';
import store from '~/store/index';
import pinia from '~/store/pinia';
import plugins from '~/plugins';
import { apolloClient, uploadFileClient } from '~/plugins/apollo';
import isBrowserSupported from '~/helpers/browser';

// Global fallback for Apollo composables called outside a component setup (event handlers,
// composables run after an await, store): set once here instead of in each component.
provideApolloClient(apolloClient);

const app = createApp({
  setup() {
    provide(
      ApolloClients,
      {
        default: apolloClient,
        uploadFileClient,
      },
    );
  },
  render: () => h(App),
});
app.use(pinia);
app.use(store);
app.use(plugins);
app.use(router);

/**
 * Check if the event is an ApolloError
 * @param {*} event
 */
const isApolloError = (event, message = null) => {
  const exceptions = event?.exception?.values ?? [];

  return exceptions.some((exc) => {
    if (exc.type !== 'ApolloError') {
      return false;
    }

    if (message === null) {
      return true;
    }

    return typeof exc.value === 'string' && exc.value.includes(message);
  });
};

sentryInit({
  app,
  dsn: window.keyprod.SENTRY_DSN,
  beforeSend: (event, hint) => {
    if (!isBrowserSupported()) {
      return null;
    }

    // We dont need to send "ApolloError: Failed to fetch" errors
    if (isApolloError(event, 'Failed to fetch')) {
      return null;
    }

    const error = hint.originalException;
    if (error
      && (
        (error.message && error.message.includes('409'))
        || (error.networkError && error.networkError.statusCode === 409)
      )
    ) {
      return null;
    }

    const eventWithUser = event;
    if (store.state.auth.user) {
      eventWithUser.user = {
        id: store.state.auth.user.id,
        email: store.state.auth.user.email,
        access_code: store.state.auth.user.access_code,
      };
    }

    return eventWithUser;
  },
  environment: window.keyprod.SENTRY_ENVIRONMENT,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: vueRouterInstrumentation(router),
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: window.keyprod.SENTRY_TRACES_SAMPLE_RATE,

  tracePropagationTargets: [window.location.origin],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

setTag('tenant', window.tenant.name);

app.mount('#app');

// Load Sentry Replay integration after mount to keep it out of the initial bundle
import('@sentry/replay').then(({ Replay }) => {
  getClient()?.addIntegration(new Replay({
    maskAllText: false,
    maskAllInputs: true,
  }));
});

// Load and init Mixpanel after mount — analytics is not needed for initial render
if (window.keyprod.MIXPANEL_TOKEN && window.keyprod.MIXPANEL_TOKEN !== '' && window.keyprod.MIXPANEL_HOST && window.keyprod.MIXPANEL_HOST !== '') {
  import('mixpanel-browser').then(({ default: mixpanel }) => {
    mixpanel.init(window.keyprod.MIXPANEL_TOKEN, {
      api_host: `https://${window.keyprod.MIXPANEL_HOST}`,
      debug: window.keyprod.APP_ENV === 'local',
      track_pageview: false,
      record_sessions_percent: 5, // 1 = 1% of sessions max
      record_heatmap_data: true, // Enable Heatmap data collection
      autocapture: {
        pageview: 'full-url',
        click: true, // click tracking enabled
        input: true,
        scroll: true,
        submit: true,
        capture_text_content: false,
      },
      persistence: 'localStorage',
      record_idle_timeout_ms: 1800000, // 3 minutes
      record_max_ms: 3600000, // 1 hour
      record_canvas: true,
      ignore_dnt: true, // ignore "Do Not Track"
      record_mask_text_selector: '', // Nothing to hide
      record_block_selector: '', // Nothing to hide
    });
  });
}
